import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { consumePostOnboardingRedirect } from "@/utils/postSignupRedirect";
import axios from "axios";
import { ArrowRight, ArrowLeft, CheckCircle, Zap } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ── ASSI Level Definitions ─────────────────────────────────────────────────
export const ASSI_LEVELS = [
  { min: 1.0, max: 2.0, label: "Complete Beginner", desc: "Never played or fewer than 5 times. Still learning the rules.", color: "bg-gray-100 border-gray-300 text-gray-700" },
  { min: 2.0, max: 3.0, label: "New Player", desc: "Know the basic rules. Working on getting the ball/birdie over the net consistently.", color: "bg-blue-50 border-blue-300 text-blue-800" },
  { min: 3.0, max: 4.0, label: "Improving Beginner", desc: "Rallies occasionally. Learning directional control and service.", color: "bg-indigo-50 border-indigo-300 text-indigo-800" },
  { min: 4.0, max: 5.0, label: "Intermediate", desc: "Can rally consistently. Know strategy basics. Play socially most weekends. (Tennis: NTRP 3.0–3.5)", color: "bg-green-50 border-green-300 text-green-800" },
  { min: 5.0, max: 6.0, label: "Solid Intermediate", desc: "Directional control, decent serve, beginning competitive play. (Tennis: NTRP 3.5–4.0)", color: "bg-teal-50 border-teal-300 text-teal-800" },
  { min: 6.0, max: 7.0, label: "Advanced Intermediate", desc: "Tournament-tested. Consistent under pressure. Club team level. (Tennis: NTRP 4.0–4.5)", color: "bg-brand-orange/10 border-brand-orange/40 text-brand-orange" },
  { min: 7.0, max: 8.0, label: "Advanced", desc: "Competitive club or league player. High performance recreational. (Tennis: NTRP 4.5–5.0)", color: "bg-orange-50 border-orange-400 text-orange-800" },
  { min: 8.0, max: 9.0, label: "Expert", desc: "Collegiate, ex-professional, or high-performance training background. (Tennis: NTRP 5.0–6.0)", color: "bg-red-50 border-red-400 text-red-800" },
  { min: 9.0, max: 10.0, label: "Elite", desc: "Professional level or international competitive athlete.", color: "bg-brand-navy/10 border-brand-navy text-brand-navy" },
];

const SPORTS = [
  { value: "tennis", label: "Tennis", emoji: "🎾" },
  { value: "pickleball", label: "Pickleball", emoji: "🏓" },
  { value: "golf", label: "Golf", emoji: "⛳" },
  { value: "padel", label: "Padel", emoji: "🏸" },
  { value: "squash", label: "Squash", emoji: "🟡" },
  { value: "badminton", label: "Badminton", emoji: "🏸" },
  { value: "climbing", label: "Climbing", emoji: "🧗" },
  { value: "table-tennis", label: "Table Tennis", emoji: "🏓" },
];

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const TIMES = ["Morning (6–11am)", "Midday (11am–2pm)", "Afternoon (2–6pm)", "Evening (6–10pm)"];

const TOTAL_STEPS = 5;

function StepIndicator({ current }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-8" aria-label={`Step ${current} of ${TOTAL_STEPS}`}>
      {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
        <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i < current ? "w-8 bg-brand-green" : i === current - 1 ? "w-12 bg-brand-orange" : "w-8 bg-gray-200"}`} />
      ))}
    </div>
  );
}

export default function OnboardingPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);

  const [sport, setSport] = useState("");
  const [assi, setAssi] = useState(null);        // float 1.0–10.0
  const [city, setCity] = useState(user?.city || "");
  const [availability, setAvailability] = useState({ days: [], times: [] });

  const canNext = () => {
    if (step === 1) return !!sport;
    if (step === 2) return assi !== null;
    if (step === 3) return city.trim().length > 1;
    if (step === 4) return availability.days.length > 0;
    return true;
  };

  const toggleDay = (d) => setAvailability((a) => ({
    ...a, days: a.days.includes(d) ? a.days.filter((x) => x !== d) : [...a.days, d]
  }));
  const toggleTime = (t) => setAvailability((a) => ({
    ...a, times: a.times.includes(t) ? a.times.filter((x) => x !== t) : [...a.times, t]
  }));

  const finish = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/api/users/me`, {
        sports: [{ sport, skill_level: String(assi) }],
        assi_score: assi,
        city,
        availability: { days: availability.days, times: availability.times },
        onboarding_completed: true,
      }, { headers });
      await refreshUser?.();
      navigate(consumePostOnboardingRedirect() || "/find-partners");
    } catch {
      navigate(consumePostOnboardingRedirect() || "/find-partners");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-12 pt-20">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-6">
          <img src="/logo.png" alt="ActivitySports" className="h-10 w-auto object-contain mx-auto mb-4" />
          <p className="text-sm text-gray-500">
            Step {step} of {TOTAL_STEPS} — <span className="text-brand-navy font-semibold">{
              ["Pick your sport", "Your skill level", "Your city", "When you play", "Ready to match"][step - 1]
            }</span>
          </p>
        </div>

        <StepIndicator current={step} />

        <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-sm min-h-[380px] flex flex-col">

          {/* ─── Step 1: Sport ─── */}
          {step === 1 && (
            <div className="flex-1">
              <h2 className="text-2xl font-heading font-bold text-brand-navy mb-1">What's your main sport?</h2>
              <p className="text-gray-500 text-sm mb-5">You can add more sports later in your profile.</p>
              <div className="grid grid-cols-2 gap-3">
                {SPORTS.map((s) => (
                  <button
                    key={s.value}
                    onClick={() => setSport(s.value)}
                    data-testid={`sport-option-${s.value}`}
                    className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                      sport === s.value
                        ? "border-brand-orange bg-brand-orange/5 text-brand-navy"
                        : "border-gray-200 hover:border-brand-orange/40 text-gray-700"
                    }`}
                  >
                    <span className="text-2xl" aria-hidden="true">{s.emoji}</span>
                    <span className="font-medium text-sm">{s.label}</span>
                    {sport === s.value && <CheckCircle size={14} className="text-brand-orange ml-auto" />}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ─── Step 2: ASSI ─── */}
          {step === 2 && (
            <div className="flex-1">
              <h2 className="text-2xl font-heading font-bold text-brand-navy mb-1">What's your skill level?</h2>
              <p className="text-gray-500 text-sm mb-5">
                This is your <strong>ASSI score</strong> — Activity Sports Skill Index (1–10). Be honest — it helps you find better matches.
              </p>
              <div className="space-y-2 max-h-[340px] overflow-y-auto pr-1">
                {ASSI_LEVELS.map((lvl) => {
                  const mid = (lvl.min + lvl.max) / 2;
                  const isSelected = assi !== null && assi >= lvl.min && (assi < lvl.max || (lvl.max === 10.0 && assi === 10.0));
                  return (
                    <button
                      key={lvl.label}
                      onClick={() => setAssi(mid)}
                      data-testid={`assi-level-${lvl.label.toLowerCase().replace(/ /g,"-")}`}
                      className={`w-full text-left p-3 rounded-xl border-2 transition-all ${
                        isSelected ? `${lvl.color} border-2` : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-sm">{lvl.label}</p>
                          <p className="text-xs opacity-80 mt-0.5">{lvl.desc}</p>
                        </div>
                        <div className="flex-shrink-0 ml-3">
                          <span className="text-xs font-mono font-bold opacity-60">{lvl.min}–{lvl.max}</span>
                          {isSelected && <CheckCircle size={14} className="text-brand-orange ml-2 inline" />}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ─── Step 3: City ─── */}
          {step === 3 && (
            <div className="flex-1">
              <h2 className="text-2xl font-heading font-bold text-brand-navy mb-1">Where do you play?</h2>
              <p className="text-gray-500 text-sm mb-5">We'll find partners near you. You can enable Travel Mode later to play in any city.</p>
              <label htmlFor="Your-City" className="block text-sm font-medium text-brand-navy mb-1.5">Your city</label><input id="Your-City"
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="e.g. Fort Worth, TX"
                data-testid="onboarding-city-input"
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-brand-navy placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green transition-colors"
              />
              <p className="text-xs text-gray-400 mt-2">Format: City, State — e.g. "Dallas, TX"</p>
            </div>
          )}

          {/* ─── Step 4: Availability ─── */}
          {step === 4 && (
            <div className="flex-1">
              <h2 className="text-2xl font-heading font-bold text-brand-navy mb-1">When do you like to play?</h2>
              <p className="text-gray-500 text-sm mb-4">Select all that apply.</p>
              <div className="mb-5">
                <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Days</p>
                <div className="flex flex-wrap gap-2">
                  {DAYS.map((d) => (
                    <button
                      key={d}
                      onClick={() => toggleDay(d)}
                      data-testid={`avail-day-${d.toLowerCase()}`}
                      className={`px-3 py-1.5 rounded-lg border-2 text-sm font-medium transition-all ${
                        availability.days.includes(d)
                          ? "bg-brand-green/10 border-brand-green text-brand-green"
                          : "border-gray-200 text-gray-600 hover:border-gray-300"
                      }`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Times</p>
                <div className="space-y-2">
                  {TIMES.map((t) => (
                    <button
                      key={t}
                      onClick={() => toggleTime(t)}
                      data-testid={`avail-time-${t.toLowerCase().replace(/ /g,"-")}`}
                      className={`w-full px-4 py-2 rounded-xl border-2 text-sm font-medium text-left transition-all ${
                        availability.times.includes(t)
                          ? "bg-brand-green/10 border-brand-green text-brand-green"
                          : "border-gray-200 text-gray-600 hover:border-gray-300"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ─── Step 5: Done ─── */}
          {step === 5 && (
            <div className="flex-1 flex flex-col items-center justify-center text-center">
              <div className="w-20 h-20 bg-brand-green/10 rounded-full flex items-center justify-center mb-5">
                <Zap size={36} className="text-brand-green" />
              </div>
              <h2 className="text-2xl font-heading font-bold text-brand-navy mb-2">You're all set!</h2>
              <p className="text-gray-500 text-sm mb-2">Here's your profile summary:</p>
              <div className="bg-gray-50 rounded-xl p-4 text-left w-full space-y-2 text-sm mt-2">
                <div className="flex justify-between"><span className="text-gray-500">Sport</span><span className="font-semibold text-brand-navy capitalize">{sport}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">ASSI Score</span><span className="font-semibold text-brand-orange">{assi} / 10</span></div>
                <div className="flex justify-between"><span className="text-gray-500">City</span><span className="font-semibold text-brand-navy">{city}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Days</span><span className="font-semibold text-brand-navy">{availability.days.join(", ") || "Any"}</span></div>
              </div>
            </div>
          )}

          {/* Nav buttons */}
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
            <button
              onClick={() => setStep((s) => s - 1)}
              disabled={step === 1}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-500 hover:text-brand-navy disabled:opacity-30 transition-colors"
            >
              <ArrowLeft size={16} /> Back
            </button>
            {step < TOTAL_STEPS ? (
              <button
                onClick={() => setStep((s) => s + 1)}
                disabled={!canNext()}
                data-testid="onboarding-next-btn"
                className="flex items-center gap-2 px-6 py-2.5 bg-brand-orange text-white font-bold rounded-xl hover:bg-brand-orange-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                Continue <ArrowRight size={16} />
              </button>
            ) : (
              <button
                onClick={finish}
                disabled={saving}
                data-testid="onboarding-finish-btn"
                className="flex items-center gap-2 px-6 py-2.5 bg-brand-green text-brand-navy font-bold rounded-xl hover:bg-brand-green/90 transition-colors shadow-sm"
              >
                {saving ? "Saving…" : "Find My Matches"} <ArrowRight size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
